"use client"

import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetDescription,
  BottomSheetTitle,
} from "@/components/ui/bottom-sheet"
import { createClient } from "@/lib/supabase/client"
import { validatePassword } from "@/lib/auth/password-policy"
import { GoogleAuthButton } from "@/components/auth/google-auth-button"
import { AppleAuthButton } from "@/components/auth/apple-auth-button"
import { ChevronRight, Loader2 } from "lucide-react"
import { trackMeta } from "@/lib/meta-pixel"
import { trackTikTok } from "@/lib/tiktok-pixel"
import { claimAttribution } from "@/lib/attribution-client"
import { newSignupEventId, reportSignupToMeta } from "@/lib/meta-capi-client"
import { Lozenge } from "@/components/ui/lozenge"

// Shared with the /auth pages — BOOTH-system pill inputs and the crisp
// near-flat premium dark pill.
const fieldClass =
  "h-10 w-full rounded-full border border-white/10 bg-white/[0.03] px-4 text-[13px] text-white placeholder:text-neutral-500 outline-none transition-all focus:border-emerald-500/50 focus:bg-white/[0.05] focus:ring-2 focus:ring-emerald-500/10"

const pillGradient = { background: "linear-gradient(to bottom, #1B1B1D, #0F0F10)" }

type Mode = "signup" | "login"

export function AuthModal({
  open,
  onOpenChange,
  defaultMode = "signup",
  defaultRole = "barber",
  dismissible = true,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  defaultMode?: Mode
  // Which role tile the signup form preselects. Booking surfaces pass
  // "client" (PRD §11 — the default public audience there is a booking
  // client); everywhere else keeps the barber-first default.
  defaultRole?: "barber" | "owner" | "client"
  // When false, a click on the backdrop or Escape does NOT close the modal —
  // only the explicit X does. Used by the signup wall so a stray click in the
  // black area around the modal doesn't accidentally bounce the visitor to the
  // landing page (the wall's close handler routes home).
  dismissible?: boolean
}) {
  const t = useTranslations("pages.auth.modal")
  const tPolicy = useTranslations("pages.auth.passwordPolicy")
  // Reuse the login page's copy rather than duplicating the string under
  // modal.* — one translation to keep in sync, not two.
  const tLogin = useTranslations("pages.auth.login")
  const router = useRouter()
  const [mode, setMode] = useState<Mode>(defaultMode)

  // useState only honors the initial defaultMode. Without this, opening
  // the modal a second time with a different defaultMode (e.g. tapping
  // 'Sign in' after previously tapping 'Create account') would inherit
  // the previous mode. Resync on every open.
  useEffect(() => {
    if (open) setMode(defaultMode)
  }, [open, defaultMode])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Signup fields
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [role, setRole] = useState<"barber" | "owner" | "client">(defaultRole)

  // Resync like defaultMode above — reopening from a different surface
  // (e.g. a booking page passing "client") must not inherit a stale role.
  useEffect(() => {
    if (open) setRole(defaultRole)
  }, [open, defaultRole])

  // Shared
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  // Matches the /auth/login default: persist unless the user opts out. Without
  // this control the modal had no way to decline a 400-day session.
  const [rememberMe, setRememberMe] = useState(true)

  const reset = () => {
    setFirstName("")
    setLastName("")
    setRole("barber")
    setEmail("")
    setPassword("")
    setRememberMe(true)
    setError(null)
    setLoading(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (mode === "login") {
        // Same server route the /auth/login page uses — rate-limited, generic
        // failure message, and it applies the Remember-me choice to the auth
        // cookie lifetime. Previously this called Supabase directly from the
        // browser, which skipped all three.
        localStorage.setItem("booth_remember_me", String(rememberMe))

        const res = await fetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ identifier: email, password, rememberMe }),
        })
        const result = await res.json()
        if (!res.ok) {
          setError(result.error || t("genericError"))
          setLoading(false)
          return
        }
        if (result.role) localStorage.setItem("user-role", result.role)
        // Full reload so server components and the role provider pick up the
        // new auth cookie.
        window.location.reload()
        return
      }

      const supabase = createClient()

      // Same 12-char policy the /auth/signup pages enforce (lib/auth/password-policy).
      // The server route rejects weak passwords regardless, but checking here keeps
      // the failure inline + localized instead of bouncing an English API error back.
      const pw = validatePassword(password)
      if (!pw.ok) {
        setError(tPolicy(pw.code))
        setLoading(false)
        return
      }

      // Signup — server route creates the user, then we sign in
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, firstName, lastName, phone: "", role }),
      })
      const result = await res.json()
      if (!res.ok) {
        setError(result.error || t("createFailed"))
        setLoading(false)
        return
      }

      // Primary conversion event for the Meta campaigns. Fired here — after
      // the account exists but before the redirect — so it lands whether or
      // not the follow-up sign-in succeeds. `content_category` lets Ads
      // Manager break results out by which side of the marketplace signed up
      // (the owner ads and the barber ads share this one event).
      const metaEventId = newSignupEventId()
      trackMeta("CompleteRegistration", { content_category: role, status: true }, metaEventId)
      trackTikTok("CompleteRegistration", { content_type: "product", content_id: role })

      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })
      if (signInError) {
        router.push("/auth/login?message=Account+created")
        return
      }

      // Attribution is claimed here, not next to the pixel above: the pixel
      // deliberately fires before sign-in, and the claim route reads the
      // caller's session to decide whose row to write.
      claimAttribution()
      reportSignupToMeta(metaEventId)
      // Clients skip pro onboarding — straight to their dashboard.
      router.push(role === "client" ? "/dashboard/client" : "/auth/onboarding")
    } catch (err) {
      setError(err instanceof Error ? err.message : t("genericError"))
      setLoading(false)
    }
  }

  return (
    <BottomSheet
      open={open}
      onOpenChange={(v) => {
        if (!v) reset()
        onOpenChange(v)
      }}
    >
      {/* Same bottom sheet the inquiry flows use — a visitor who taps Inquire
          while logged out gets one shape, not a sheet followed by a centred
          dialog. */}
      <BottomSheetContent
        className="px-6 pb-8 sm:px-8"
        showCloseButton={dismissible}
        // Frosted backdrop — the real page (and navbar) blur behind the sheet
        // so it teases a signup instead of hiding everything behind flat black.
        overlayClassName="bg-black/40 backdrop-blur-lg"
        // Don't autofocus the first control on open — it paints a focus ring
        // on the Google button before the user has interacted. Tab still works.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={dismissible ? undefined : (e) => e.preventDefault()}
        onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
      >
        {/* BOOTH mark — centered at the top of the modal. */}
        <div className="flex justify-center mb-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/images/booth-logo-white.png" alt="BOOTH" className="h-[26px] w-auto" />
        </div>
        <div className="text-center space-y-2">
          <BottomSheetDescription asChild>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-medium text-neutral-300">
              <Lozenge className="w-2.5" />
              {mode === "signup" ? t("signupSubtitle") : t("loginSubtitle")}
            </span>
          </BottomSheetDescription>
          <BottomSheetTitle className="text-2xl font-semibold tracking-tight text-white">
            {mode === "signup" ? t("signupTitle") : t("loginTitle")}
          </BottomSheetTitle>
        </div>

        <div className="mt-4 space-y-4">
          {mode === "signup" && (
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  { value: "barber", label: t("imBarber") },
                  { value: "owner", label: t("iOwnShop") },
                  { value: "client", label: "Client" },
                ] as const
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={role === option.value}
                  onClick={() => setRole(option.value)}
                  style={role === option.value ? pillGradient : undefined}
                  className={
                    role === option.value
                      ? "flex h-9 items-center justify-center rounded-full border border-white/15 text-xs font-medium text-white shadow-sm transition-all outline-none focus-visible:ring-2 focus-visible:ring-white/20"
                      : "flex h-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.02] text-xs font-medium text-neutral-500 transition-all hover:text-neutral-300 outline-none focus-visible:ring-2 focus-visible:ring-white/20"
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          <AppleAuthButton
            label={mode === "signup" ? t("appleSignup") : t("appleLogin")}
            errorFallback={t("appleError")}
            role={mode === "signup" ? role : undefined}
            onError={setError}
          />

          <GoogleAuthButton
            label={mode === "signup" ? t("googleSignup") : t("googleLogin")}
            errorFallback={t("googleError")}
            role={mode === "signup" ? role : undefined}
            onError={setError}
          />

          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] tracking-[0.3em] text-neutral-600">{t("or")}</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "signup" && (
              <div className="grid grid-cols-2 gap-2">
                <input
                  placeholder={t("firstName")}
                  aria-label={t("firstName")}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className={fieldClass}
                />
                <input
                  placeholder={t("lastName")}
                  aria-label={t("lastName")}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className={fieldClass}
                />
              </div>
            )}

            <input
              type="email"
              placeholder={t("email")}
              aria-label={t("email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={fieldClass}
            />
            {/* No minLength here: signup complexity is enforced by
                validatePassword() on submit (and again server-side), and login
                must stay open to legacy accounts with shorter passwords on file. */}
            <input
              type="password"
              placeholder={t("password")}
              aria-label={t("password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={fieldClass}
            />
            {mode === "signup" && !error && (
              <p className="text-[10px] text-neutral-600">{tPolicy("requirements")}</p>
            )}

            {mode === "login" && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="modalRememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-3.5 w-3.5 cursor-pointer rounded border-neutral-600 bg-[#0a0a0a] text-[#0F8843] transition-all focus:ring-2 focus:ring-[#0F8843]/20"
                />
                <label htmlFor="modalRememberMe" className="cursor-pointer select-none text-[11px] text-neutral-400">
                  {tLogin("rememberMe")}
                </label>
              </div>
            )}

            {error && (
              <p className="text-[11px] text-red-400" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              style={pillGradient}
              className="flex h-10 w-full items-center justify-center gap-1.5 rounded-full border border-white/10 text-xs font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {mode === "signup" ? t("creating") : t("signingIn")}
                </span>
              ) : (
                <>
                  {mode === "signup" ? t("createAccount") : t("signIn")}
                  <ChevronRight className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          </form>

          <button
            type="button"
            onClick={() => {
              setMode((m) => (m === "signup" ? "login" : "signup"))
              setError(null)
            }}
            className="text-[11px] text-neutral-400 hover:text-white transition-colors w-full text-center"
          >
            {mode === "signup" ? (
              <>{t("haveAccount")} <span className="text-white font-semibold">{t("switchSignIn")}</span></>
            ) : (
              <>{t("newHere")} <span className="text-white font-semibold">{t("switchCreate")}</span></>
            )}
          </button>
        </div>
      </BottomSheetContent>
    </BottomSheet>
  )
}
